import React, { Component, type ErrorInfo, type ReactNode, useEffect, useState } from 'react';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';
import App from '@src/App';
import { enableRtlSupport } from '@shared/i18n/locale';
import { generated_pwa_client } from './config/generatedClient';
import './pwa.css';

type BoundaryProps = { children: ReactNode };
type BoundaryState = { error: Error | null };

/** Surfaces render crashes instead of a blank white page. */
class PwaErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[PWA] render crash', error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <View style={styles.boot}>
          <Text style={styles.errorTitle}>Something went wrong</Text>
          <Text style={styles.errorBody}>{this.state.error.message}</Text>
          <Text
            style={styles.retry}
            onPress={() => {
              this.setState({ error: null });
              window.location.reload();
            }}
          >
            Reload
          </Text>
        </View>
      );
    }
    return this.props.children;
  }
}

/**
 * PWA root — mounts the real native App tree (same screens / navigation).
 * Boot gates are skipped here so Auth MainScreen can paint on first load.
 */
export function PwaApp() {
  const [ready, set_ready] = useState(false);
  const [error, set_error] = useState<string | null>(null);

  useEffect(() => {
    try {
      enableRtlSupport();
      document.title = `${generated_pwa_client.appName} (PWA)`;
      const theme = document.querySelector('meta[name="theme-color"]');
      if (theme) {
        theme.setAttribute('content', generated_pwa_client.themeColor);
      }
      set_ready(true);
    } catch (e: unknown) {
      set_error(e instanceof Error ? e.message : String(e));
      set_ready(true);
    }
  }, []);

  if (!ready) {
    return (
      <View style={styles.boot}>
        <ActivityIndicator size="large" color={generated_pwa_client.themeColor} />
        <Text style={styles.bootLabel}>Starting {generated_pwa_client.appName}…</Text>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.boot}>
        <Text style={styles.errorTitle}>PWA failed to start</Text>
        <Text style={styles.errorBody}>{error}</Text>
      </View>
    );
  }

  return (
    <PwaErrorBoundary>
      <View style={styles.appRoot}>
        <App />
      </View>
    </PwaErrorBoundary>
  );
}

const styles = StyleSheet.create({
  appRoot: {
    flex: 1,
    width: '100%' as unknown as number,
    maxWidth: 430,
    height: '100%' as unknown as number,
    minHeight: '100%' as unknown as number,
    alignSelf: 'center',
  },
  boot: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: generated_pwa_client.backgroundColor,
    padding: 24,
    gap: 12,
    minHeight: '100%' as unknown as number,
  },
  bootLabel: {
    color: '#cbd5e1',
    fontSize: 14,
  },
  errorTitle: {
    color: '#f8fafc',
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 8,
    textAlign: 'center',
  },
  errorBody: {
    color: '#f87171',
    fontSize: 14,
    textAlign: 'center',
  },
  retry: {
    marginTop: 12,
    color: '#93c5fd',
    fontSize: 16,
    fontWeight: '600',
  },
});
